/**
 * The table's sound (review 0013, ADR 0027): masayagel's purchased recordings played by
 * its rules, and two CC0 card recordings (BMacZero, OpenGameArt "Playing Card Sounds").
 * Everything the browser gives comes in as ports, so the rules are tested without a
 * speaker. A missing or broken sound never stops the game.
 *
 * Files are `.sfx` whatever their bytes (MP3 or WAV): download managers grab `.mp3`
 * requests and in one browser the sound never played (masayagel decision 272);
 * `decodeAudioData` reads the bytes, not the name.
 */

export type Sample =
  | "kart-at"
  | "kart-dagit"
  | "sira-bende"
  | "el-bitti"
  | "mac-sonu"
  | "sure-tik"
  | "sure-bitiyor";
export type Cue = Exclude<Sample, "sure-bitiyor">;

/** Gains assume the recordings peak at -3 dBFS; the tick plays every second, so it whispers. */
const GAIN: Record<Sample, number> = {
  "kart-at": 0.6,
  "kart-dagit": 0.6,
  "sira-bende": 0.55,
  "el-bitti": 0.55,
  "mac-sonu": 0.6,
  "sure-tik": 0.16,
  "sure-bitiyor": 0.3,
};

const SAMPLES = Object.keys(GAIN) as Sample[];
const url = (name: Sample) => `/sounds/${name}.sfx`;

/** From a tab nobody looks at, only what calls you back (masayagel v2). */
const CALLS_YOU: ReadonlySet<string> = new Set(["sira-bende", "mac-sonu"]);

/**
 * Only for someone looking elsewhere: at the table the turn is already seen (the pod turns
 * white, the panel opens), and heard on every turn the recording grated (owner, 13 Sept 2026).
 */
const ONLY_WHEN_AWAY: ReadonlySet<string> = new Set(["sira-bende"]);

/** Where a device starts: under full, since players sit in Discord's voice chat too. */
export const DEFAULT_VOLUME = 0.7;

const clampVolume = (value: number): number =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, Math.round(value * 100) / 100)) : DEFAULT_VOLUME;

/** The same sound twice inside this window is one: bots playing in a row must not crack. */
const REPEAT_MS = 60;

interface ParamLike {
  value: number;
  setValueAtTime: (value: number, at: number) => void;
  exponentialRampToValueAtTime: (value: number, at: number) => void;
}
interface NodeLike {
  connect: (target: unknown) => void;
}
interface SourceLike extends NodeLike {
  buffer: AudioBuffer | null;
  start: (at?: number) => void;
  stop: (at?: number) => void;
  onended: (() => void) | null;
}
interface GainLike extends NodeLike {
  gain: ParamLike;
}
export interface AudioLike {
  currentTime: number;
  destination: unknown;
  state: string;
  resume: () => Promise<void> | void;
  close: () => Promise<void> | void;
  createBufferSource: () => SourceLike;
  createGain: () => GainLike;
  decodeAudioData: (data: ArrayBuffer) => Promise<AudioBuffer>;
}

export interface SoundPorts {
  /** Made on the first gesture: a browser allows no sound before one. */
  audio: () => AudioLike | null;
  load: (url: string) => Promise<ArrayBuffer | null>;
  visible: () => boolean;
  /** Looking at the table: the page is visible and has the focus. */
  attentive: () => boolean;
  now: () => number;
  /** The player's volume on this device, 0 to 1; null when never set. */
  readVolume: () => number | null;
  writeVolume: (volume: number) => void;
  /** Development only: what was decided, where a test browser has no speaker. */
  mark?: (what: string) => void;
}

export interface Sounds {
  prime: () => void;
  preload: () => void;
  /** `delayMs` from now: a card is heard as it lands, not as it leaves. */
  play: (cue: Cue, delayMs?: number) => void;
  /** The urgent countdown bed, which runs until it is stopped. */
  bed: (on: boolean) => void;
  /** 0 to 1 for the whole table; 0 is silence (review 0013, decision 64 revised). */
  volume: () => number;
  setVolume: (volume: number) => void;
  subscribe: (listener: () => void) => () => void;
}

export function createSounds(ports: SoundPorts): Sounds {
  let context: AudioLike | null = null;
  let volume = clampVolume(ports.readVolume() ?? DEFAULT_VOLUME);
  /** Every sound passes here, so the slider reaches what is already playing. */
  let master: GainLike | null = null;
  const listeners = new Set<() => void>();
  const bytes = new Map<Sample, Promise<ArrayBuffer | null>>();
  const buffers = new Map<Sample, AudioBuffer>();
  const lastPlayed = new Map<string, number>();
  let bedNow: { source: SourceLike; gain: GainLike } | null = null;
  let bedToken = 0;

  const open = (): AudioLike | null => {
    context ??= ports.audio();
    if (context && master === null) {
      master = context.createGain();
      master.gain.value = volume;
      master.connect(context.destination);
    }
    // Suspended before a gesture, or "interrupted" by the phone (a call, the lock screen): ask it
    // to carry on. A browser may refuse until the next gesture; that is no error worth reporting.
    if (context && context.state !== "running" && context.state !== "closed")
      void Promise.resolve(context.resume()).catch(() => undefined);
    return context;
  };

  const fetchAll = (): void => {
    for (const name of SAMPLES) if (!bytes.has(name)) bytes.set(name, ports.load(url(name)));
  };

  const bufferOf = async (ctx: AudioLike, name: Sample): Promise<AudioBuffer | null> => {
    const ready = buffers.get(name);
    if (ready) return ready;
    if (!bytes.has(name)) fetchAll();
    const data = await bytes.get(name);
    if (!data) return null;
    try {
      // decodeAudioData consumes the buffer; a second call would find it empty.
      const decoded = await ctx.decodeAudioData(data.slice(0));
      buffers.set(name, decoded);
      return decoded;
    } catch {
      return null;
    }
  };

  const connectPlay = (ctx: AudioLike, buffer: AudioBuffer, gainValue: number, delayMs = 0) => {
    const source = ctx.createBufferSource();
    const gain = ctx.createGain();
    gain.gain.value = gainValue;
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(master ?? ctx.destination);
    source.start(ctx.currentTime + delayMs / 1_000);
    return { source, gain };
  };

  /** Shared gate for every sound: silent, hidden tab, or the same sound a moment ago. */
  const allowed = (what: string): boolean => {
    if (volume === 0 || (!ports.visible() && !CALLS_YOU.has(what))) return false;
    if (ONLY_WHEN_AWAY.has(what) && ports.attentive()) return false;
    const now = ports.now();
    const last = lastPlayed.get(what);
    if (last !== undefined && now - last < REPEAT_MS) return false;
    lastPlayed.set(what, now);
    return true;
  };

  const stopBed = (): void => {
    bedToken += 1;
    const current = bedNow;
    bedNow = null;
    if (!current) return;
    // A hard stop leaves a click: 80 ms down, then silence.
    const at = context?.currentTime;
    if (at !== undefined) {
      current.gain.gain.setValueAtTime(Math.max(current.gain.gain.value, 0.0001), at);
      current.gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.08);
    }
    try {
      current.source.stop(at === undefined ? undefined : at + 0.1);
    } catch {
      // already finished
    }
  };

  const startBed = (): void => {
    const ctx = volume === 0 ? null : open();
    if (!ctx || bedNow) return;
    const token = ++bedToken;
    void bufferOf(ctx, "sure-bitiyor").then((buffer) => {
      // The band may have closed, or sound been turned off, while it decoded.
      if (!buffer || token !== bedToken || bedNow || volume === 0) return;
      const started = connectPlay(ctx, buffer, GAIN["sure-bitiyor"]);
      started.source.onended = () => {
        if (bedNow?.source === started.source) bedNow = null;
      };
      bedNow = started;
    });
  };

  const notify = () => {
    for (const listener of listeners) listener();
  };

  return {
    prime: () => {
      open();
    },
    preload: fetchAll,
    play: (cue, delayMs = 0) => {
      if (!allowed(cue)) return;
      const ctx = open();
      if (!ctx) return;
      const asked = ports.now();
      void bufferOf(ctx, cue).then((buffer) => {
        // Nothing queued from before sound was allowed: late by a second is not at all.
        if (!buffer || volume === 0 || ports.now() - asked >= 1_000) return;
        connectPlay(ctx, buffer, GAIN[cue], delayMs);
        ports.mark?.(cue);
      });
    },
    bed: (on) => {
      ports.mark?.(on ? "sure-bitiyor" : "sure-bitiyor:dur");
      if (on) startBed();
      else stopBed();
    },
    volume: () => volume,
    setVolume: (value) => {
      volume = clampVolume(value);
      if (master && context) master.gain.setValueAtTime(volume, context.currentTime);
      if (volume === 0) stopBed();
      ports.writeVolume(volume);
      notify();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
